/**
 * What each portfolio photo shows: its alt text, and the file name it should
 * have.
 *
 * Photos arrived named by whatever took them (IMG_1149.webp, Facebook's
 * 487225866_..._n.jpg, the uploader's random UUIDs) and every one of them
 * rendered in the portfolio grid with alt="". Search engines read both the alt
 * text and the file name to learn what an image is, so the portfolio told
 * them nothing.
 *
 * The renames are applied from Admin → Content ("Apply new file names"). It
 * COPIES each file to its new name and leaves the old one where it is, because
 * the homepage and project pages link some photos by their old names. The
 * listing shows only the new copy (see visiblePortfolioKeys). Once those pages
 * point at the new names, the old copies can go.
 *
 * Free of server imports: the portfolio grid uses altFor() in the browser.
 */

export type PortfolioPhoto = {
  /** Bucket key the photo has today. */
  from: string;
  /** Bucket key it should have. null hides it: a duplicate of another photo. */
  to: string | null;
  alt: string;
};

export const PORTFOLIO_PHOTOS: PortfolioPhoto[] = [
  {
    from: "portfolio/decals/494712253_29339269889051593_5253221976569565079_n.jpg",
    to: "portfolio/decals/sports-medicine-door-decal.jpg",
    alt: "Sports Medicine vinyl decal with a block A logo on a black locker room door",
  },
  {
    from: "portfolio/embroidery/IMG_0667.jpg",
    to: "portfolio/embroidery/city-of-louisa-fire-department-embroidered-polo.jpg",
    alt: "City of Louisa Fire Department polo with an embroidered department patch and firefighter name",
  },
  {
    from: "portfolio/embroidery/IMG_0669.webp",
    to: "portfolio/embroidery/desco-logo-embroidered-shirt.webp",
    alt: "DESCO logo embroidered on a purple button-up shirt",
  },
  {
    from: "portfolio/embroidery/IMG_0845.webp",
    to: "portfolio/embroidery/gscs-globe-logo-embroidered-shirt.webp",
    alt: "GSCS globe logo embroidered on a royal blue shirt",
  },
  {
    from: "portfolio/embroidery/IMG_0883.webp",
    to: "portfolio/embroidery/block-a-logo-embroidered-backpack.webp",
    alt: "Block A logo embroidered on a grey backpack",
  },
  {
    from: "portfolio/embroidery/IMG_1149.webp",
    to: "portfolio/embroidery/slim-chickens-logo-embroidered-hi-vis-jacket.webp",
    alt: "Slim Chickens logo embroidered on a hi-vis safety jacket",
  },
  {
    from: "portfolio/embroidery/IMG_1151.webp",
    to: "portfolio/embroidery/clem-cup-golf-logo-embroidered-polo.webp",
    alt: "The Clem Cup golf logo embroidered on an orange polo",
  },
  {
    from: "portfolio/laser engraving/e683f3a8-1c97-411b-b440-aa558d027636.png",
    to: "portfolio/laser engraving/k-t-district-golden-key-award-plaque.png",
    alt: "K-T District Golden Key Award plaque for Bob Hillhouse, laser engraved with a gold key",
  },
  {
    from: "portfolio/screen printing/487225866_1470337330954120_1049437410294715949_n.jpg",
    to: "portfolio/screen printing/louisa-bass-club-2023-tournament-shirt.jpg",
    alt: "Louisa Bass Club 2023 tournament shirt, screen printed in full color with the sponsor list",
  },
  {
    from: "portfolio/screen printing/487364512_1470337207620799_3529098500044859689_n.jpg",
    to: "portfolio/screen printing/boyd-county-cub-house-rules-shirt.jpg",
    alt: "Boyd County Cub House Rules shirt, screen printed in full color on white",
  },
  {
    from: "portfolio/screen printing/IMG_0665.webp",
    to: "portfolio/screen printing/portsmouth-brewing-bike-night-2026-shirt.webp",
    alt: "Portsmouth Brewing and Distillery 2026 Bike Night shirt",
  },
  {
    from: "portfolio/screen printing/att.7HrrV8z0JmogHaonXqT089_Q58usTHyE6SaSL6mr0Ig.webp",
    to: "portfolio/screen printing/talk-trash-eat-good-shirt.webp",
    alt: "Talk Trash, Eat Good raccoon design printed on a grey shirt",
  },
  {
    from: "portfolio/signs & banners/487207599_1470337357620784_8184166809927746075_n.jpg",
    to: "portfolio/signs & banners/welcome-to-ashland-street-pole-banners.jpg",
    alt: "Welcome to Ashland, Kentucky street pole banners",
  },
  {
    from: "portfolio/signs & banners/488164590_1470337314287455_7173642071614516513_n.jpg",
    to: "portfolio/signs & banners/appalachian-regional-commission-welcome-street-banner.jpg",
    alt: "Appalachian Regional Commission welcome banner being hung across a downtown street",
  },
  {
    // The same file as the Golden Key plaque under laser engraving.
    from: "portfolio/trophies/plaque.png",
    to: null,
    alt: "K-T District Golden Key Award plaque",
  },
  {
    // Moves into "trophies & awards", so the gallery has one awards filter, not two.
    from: "portfolio/trophies/trophy_1.png",
    to: "portfolio/trophies & awards/making-the-difference-acrylic-flame-award.png",
    alt: "Engraved acrylic flame award, Making the Difference, presented to Governor Andy Beshear",
  },
  {
    from: "portfolio/trophies & awards/32cc40ed-032b-4a99-b971-3c6fc28d02ce.jpg",
    to: "portfolio/trophies & awards/tri-state-junior-golf-tour-gold-medal.jpg",
    alt: "Gold first place medal, Tri-State Junior Golf Tour 2026",
  },
  {
    from: "portfolio/trophies & awards/3e9bf47f-133b-4929-a7c8-2f06f4cb253e.jpg",
    to: "portfolio/trophies & awards/tri-state-junior-golf-tour-silver-medal.jpg",
    alt: "Silver second place medal, Tri-State Junior Golf Tour 2026",
  },
  {
    from: "portfolio/trophies & awards/8d59c58b-47bb-4f51-98ab-747a6eabe763.jpg",
    to: "portfolio/trophies & awards/tri-state-junior-golf-tour-bronze-medal.jpg",
    alt: "Bronze third place medal, Tri-State Junior Golf Tour 2026",
  },
  {
    from: "portfolio/vehicles/485142771_9892279594129680_8920933977878192027_n.jpg",
    to: "portfolio/vehicles/ortholinks-truck-door-lettering.jpg",
    alt: "OrthoLinks logo vinyl lettering on a white pickup truck door",
  },
  {
    from: "portfolio/vehicles/485766528_9892279360796370_2688598063375850140_n.jpg",
    to: "portfolio/vehicles/ortholinks-trailer-graphics.jpg",
    alt: "OrthoLinks and DARI Motion graphics on a white enclosed trailer",
  },
];

const byKey = new Map<string, PortfolioPhoto>();
for (const p of PORTFOLIO_PHOTOS) {
  byKey.set(p.from, p);
  if (p.to) byKey.set(p.to, p);
}

/** A bucket key, from a key or from a public URL ending in one. */
export function keyOf(urlOrKey: string): string {
  const raw = urlOrKey.split("?")[0];
  const at = raw.indexOf("portfolio/");
  const key = at >= 0 ? raw.slice(at) : raw;
  try {
    return decodeURIComponent(key);
  } catch {
    return key;
  }
}

/** A file name a camera or a random generator made, not a person. */
export function isMachineName(file: string): boolean {
  const base = file.replace(/\.[a-z0-9]+$/i, "");
  return (
    /^(img|dsc|pxl|photo|image)[_-]?\d+/i.test(base) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(base) ||
    /^\d{6,}_/.test(base) ||
    /^att\./i.test(base) ||
    !/[a-z]{3}/i.test(base)
  );
}

/**
 * Alt text for a portfolio photo.
 *
 * A photo listed above gets its written description. One uploaded since is
 * named after what it shows (the upload form asks), so its file name reads as
 * a description. Anything else at least says what kind of work it is and
 * where, which beats saying nothing.
 */
export function altFor(urlOrKey: string, category?: string): string {
  const key = keyOf(urlOrKey);
  const known = byKey.get(key);
  if (known) return known.alt;

  const file = key.split("/").pop() ?? "";
  if (file && !isMachineName(file)) {
    const words = file
      .replace(/\.[a-z0-9]+$/i, "")
      .replace(/-[0-9a-f]{6}$/i, "") // the collision suffix the uploader adds
      .replace(/[-_]+/g, " ")
      .trim();
    if (words) return words.charAt(0).toUpperCase() + words.slice(1);
  }
  const what = category ? category.replace(/\b\w/g, (c) => c.toUpperCase()) : "Custom work";
  return `${what} by Area Advertising in Ashland, KY`;
}

/**
 * Which bucket keys the gallery should show.
 *
 * Hides a photo's old copy once its new one exists, and the duplicates marked
 * to: null. Before the renames run, every old key is still shown, so nothing
 * disappears in between.
 */
export function visiblePortfolioKeys(keys: string[]): string[] {
  const present = new Set(keys);
  return keys.filter((k) => {
    const p = byKey.get(k);
    if (!p || p.from !== k) return true;
    if (p.to === null) return false;
    return !present.has(p.to);
  });
}

/** "Bob's Golden Key plaque!" → "bobs-golden-key-plaque". At most 80 characters. */
export function slugifyPhotoName(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/&/g, " and ")
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80)
    .replace(/-+$/g, "");
}
